import {Alert, Group, Paper, Stack, Text, Title, UnstyledButton, createStyles} from '@mantine/core';
import React, {useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {pluralize} from 'BenchlingUtil';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {MAX_BULK_PREDICTION_ITEMS} from 'model-hub/scientific-models/ScientificModelConstants';
import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import AddInputsMenu from './AddInputsMenu';
import EntitySearchModal, {
  type EntitySearchResult,
  type EntitySearchTab,
} from './entity-search/EntitySearchModal';
import type {FileImportResult} from './file-import/FileImport';
import InputRow, {INPUTS_GRID_COLUMNS} from './InputRow';
import InputsTablePager from './InputsTablePager';
import InputsUploadDropzone from './InputsUploadDropzone';
import {
  type ComposedCapabilities,
  getInputLabel,
  getInputStatusForCapabilities,
  getTotalCopies,
  makeDefaultInputName,
  makeEntityInputs,
  type PredictionComponent,
  type PredictionInput,
  type PredictionInputsUpdate,
  makeInput,
  validateAllInputs,
} from './newPredictionState';

const INPUTS_PER_PAGE = 10;

// Levels map 1:1 to nucleus-ui Alert variants (see ALERT_DEFAULTS in
// packages/@benchling-nucleus-ui/src/components/feedback/Alert.styles.ts), which drive the
// background/border/icon color off the `variant` prop — NOT `color`. Passing `color` alone leaves
// every alert on the default `info` variant, so success/error would look identical.
//   success   (green)  — all rows imported and valid
//   attention (yellow) — all rows imported but some sequences are invalid
//   warning   (orange) — some rows were skipped (empty)
//   error     (red)    — structural failure, nothing imported
type ImportFeedbackLevel = 'attention' | 'error' | 'success' | 'warning';

interface ImportFeedback {
  level: ImportFeedbackLevel;
  // Bold category shown above the detail message.
  title: string;
  message: string;
}

// Icons follow the nucleus InlineBanner convention (Alert.stories.tsx ICON_NAMES_BY_VARIANT).
const IMPORT_FEEDBACK_ICON: Record<ImportFeedbackLevel, BenchlingIconName> = {
  attention: 'exclamation-circle',
  error: 'exclamation-circle',
  success: 'check-circle',
  warning: 'exclamation-circle',
};

// Level rules: skipped source records -> warning (orange); all imported but some invalid -> attention
// (yellow); all imported and valid -> success (green). Every level uses a bold title + detail.
// The importer counts invalid rows model-agnostically; recompute against the composed capability set
// so the banner reflects composed validity (e.g. a ligand row is invalid when the selected models
// don't support molecules).
function buildImportFeedback(
  {inputs, summary: importSummary}: FileImportResult,
  capabilities: ComposedCapabilities
): ImportFeedback {
  const {importedComponentCount, importedInputCount, sourceRecords} = importSummary;
  const invalidInputCount = inputs.filter(
    (input) => getInputStatusForCapabilities(input.components, capabilities) === 'invalid'
  ).length;
  const summary =
    sourceRecords != null
      ? `Imported ${importedInputCount} of ${sourceRecords.count} ${pluralize(sourceRecords.count, sourceRecords.label)}.`
      : `Imported ${importedInputCount} ${pluralize(importedInputCount, 'input')} with ${importedComponentCount} ${pluralize(importedComponentCount, 'component')}.`;
  if (sourceRecords != null && sourceRecords.skippedCount > 0) {
    const title =
      sourceRecords.skippedCount === 1
        ? `A ${sourceRecords.label} was skipped`
        : `Some ${pluralize(sourceRecords.skippedCount, sourceRecords.label)} were skipped`;
    return {
      level: 'warning',
      title,
      message: `${summary} ${sourceRecords.skippedCount} empty ${pluralize(
        sourceRecords.skippedCount,
        sourceRecords.label
      )} ${sourceRecords.skippedCount === 1 ? 'was' : 'were'} skipped.`,
    };
  }
  if (invalidInputCount > 0) {
    const invalidUnit = sourceRecords?.label ?? 'input';
    return {
      level: 'attention',
      title: 'Some inputs need fixing',
      message: `${summary} ${invalidInputCount} ${pluralize(invalidInputCount, invalidUnit)} ${
        invalidInputCount === 1 ? 'has' : 'have'
      } invalid sequences to fix before submitting.`,
    };
  }
  return {level: 'success', title: 'Import complete', message: summary};
}

/**
 * Set updater that drops one input id, returning the same Set when the id isn't present so an
 * unrelated removal doesn't force a re-render.
 */
function forgetInput(inputId: string): (current: Set<string>) => Set<string> {
  return (current) => {
    if (!current.has(inputId)) {
      return current;
    }
    const next = new Set(current);
    next.delete(inputId);
    return next;
  };
}

// Picked entities become new inputs, or join the components of an existing one.
type EntitySearchTarget = {tab: EntitySearchTab} & (
  | {destination: 'inputs'}
  | {destination: 'components'; inputId: string}
);

function buildSkippedEntitiesFeedback({unavailableCount}: {unavailableCount: number}): ImportFeedback | null {
  const skippedCount = unavailableCount;
  if (skippedCount === 0) {
    return null;
  }
  const reasons: Array<{count: number; text: string}> = [];
  if (unavailableCount > 0) {
    reasons.push({
      count: unavailableCount,
      text: `${unavailableCount === 1 ? 'is' : 'are'} no longer available to you.`,
    });
  }
  return {
    level: 'warning',
    title: skippedCount === 1 ? "An entity couldn't be added" : "Some entities couldn't be added",
    // One sentence per reason, e.g. "2 of the selected entities are no longer available to you."
    message: reasons
      .map(({count, text}, index) => `${count}${index === 0 ? ' of the selected entities' : ''} ${text}`)
      .join(' '),
  };
}

interface InputsSectionProps {
  capabilities: ComposedCapabilities;
  disabled?: boolean;
  inputs: Array<PredictionInput>;
  onInputsChange: (nextInputs: PredictionInputsUpdate) => void;
  /** Offer registry entities as inputs. Needs core's search and permissions, so standalone leaves it off. */
  withRegistryInputs?: boolean;
}

export default function InputsSection({
  capabilities,
  disabled = false,
  inputs,
  onInputsChange,
  withRegistryInputs = false,
}: InputsSectionProps): JSX.Element {
  const {classes} = useStyles();
  // Newly added inputs start expanded so the user can edit immediately; imported inputs stay
  // collapsed (a large import shouldn't open every editor at once).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set(inputs.map((i) => i.id)));
  // Rows the user has focused and then left. A row's "Empty" status stays hidden until it's settled
  // (collapsed or blurred), so a brand-new row doesn't read as an error before anything is typed.
  const [blurredIds, setBlurredIds] = useState<Set<string>>(() => new Set());
  const [page, setPage] = useState(0);
  // Import feedback shown as an inline banner. The standalone SPA has no on-screen toast mount,
  // and the design surfaces errors inline within the section rather than via toasts.
  const [importFeedback, setImportFeedback] = useState<ImportFeedback | null>(null);
  const openFilePickerRef = useRef<() => void>(() => {});
  const [entitySearchTarget, setEntitySearchTarget] = useState<EntitySearchTarget | null>(null);

  // Clamp at render so deletes that shrink the list never strand the user on an empty page.
  const pageCount = Math.max(1, Math.ceil(inputs.length / INPUTS_PER_PAGE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageStart = currentPage * INPUTS_PER_PAGE;
  const pagedInputs = inputs.slice(pageStart, pageStart + INPUTS_PER_PAGE);
  const isPaginated = inputs.length > INPUTS_PER_PAGE;
  // The composed cap is the minimum across selected models; with no models selected the section is
  // disabled, so fall back to the global cap to keep importer/dropzone limits sensible.
  const maxInputs = capabilities.supportsModels
    ? capabilities.maxBulkPredictionItems
    : MAX_BULK_PREDICTION_ITEMS;
  const atInputLimit = inputs.length >= maxInputs;
  const usedInputNames = new Set(inputs.map((input) => input.name.trim()).filter((name) => name.length > 0));

  // Validate on every change so per-row statuses always reflect the current values (no manual
  // Validate step). Editing also clears any stale import banner.
  const handleInputsChange = (next: Array<PredictionInput>): void => {
    setImportFeedback(null);
    onInputsChange(validateAllInputs(next));
  };

  const handleComponentsChange = (inputId: string, components: Array<PredictionComponent>): void => {
    handleInputsChange(inputs.map((input) => (input.id === inputId ? {...input, components} : input)));
  };

  const handleNameChange = (inputId: string, name: string): void => {
    handleInputsChange(inputs.map((input) => (input.id === inputId ? {...input, name} : input)));
  };

  const handleAddInput = (): void => {
    // Each model can set a lower batch cap; file importers enforce the same resolved limit.
    if (inputs.length >= maxInputs) {
      return;
    }
    const input = makeInput(makeDefaultInputName(usedInputNames));
    const nextInputs = [...inputs, input];
    setExpandedIds((current) => new Set(current).add(input.id));
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_INPUT_ROW_ADDED, {
      totalInputs: nextInputs.length,
    });
    // The new row is appended at the end, so jump to its (last) page to keep it visible and expanded.
    setPage(Math.floor((nextInputs.length - 1) / INPUTS_PER_PAGE));
    handleInputsChange(nextInputs);
  };

  const handleRemoveInput = (inputId: string): void => {
    const nextInputs = inputs.filter((input) => input.id !== inputId);
    trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.NEW_PREDICTION_INPUT_ROW_REMOVED, {
      totalInputs: nextInputs.length,
    });
    // Input ids are never reused, so a removed row's per-row UI state can only accumulate. Drop it
    // rather than letting these Sets grow for the life of the page.
    setExpandedIds(forgetInput(inputId));
    setBlurredIds(forgetInput(inputId));
    handleInputsChange(nextInputs);
  };

  const handleToggleExpand = (inputId: string): void => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(inputId)) {
        next.delete(inputId);
      } else {
        next.add(inputId);
      }
      return next;
    });
  };

  const handleInputBlurred = (inputId: string): void => {
    setBlurredIds((current) => (current.has(inputId) ? current : new Set(current).add(inputId)));
  };

  const handleImported = (result: FileImportResult): void => {
    if (result.inputs.length === 0) {
      setImportFeedback(buildImportFeedback(result, capabilities));
      return;
    }

    const overflowCountRef = {current: null as number | null};
    let appendStartIndex = 0;
    let appended = false;

    onInputsChange((current) => {
      const combinedCount = current.length + result.inputs.length;
      if (combinedCount > maxInputs) {
        overflowCountRef.current = combinedCount;
        return current;
      }

      appendStartIndex = current.length;
      appended = true;
      return validateAllInputs([...current, ...result.inputs]);
    });

    if (overflowCountRef.current != null) {
      const combinedCount = overflowCountRef.current;
      setImportFeedback({
        level: 'error',
        title: 'Too many inputs',
        message: `Adding this file would bring the batch to ${combinedCount} inputs, but a batch can have at most ${maxInputs}.`,
      });
      return;
    }

    if (!appended) {
      return;
    }

    // Append imported rows. Parsed inputs already ran through validateAllInputs; preserve existing
    // expansion state and keep new rows collapsed. Set the banner after onInputsChange because
    // handleInputsChange clears it.
    setPage(Math.floor(appendStartIndex / INPUTS_PER_PAGE));
    setImportFeedback(buildImportFeedback(result, capabilities));
  };

  const handleImportError = (title: string, message: string): void => {
    setImportFeedback({level: 'error', title, message});
  };

  const handleSearchEntitiesForInputs = (tab: EntitySearchTab): void => {
    setEntitySearchTarget({destination: 'inputs', tab});
  };

  // The picker caps the selection at what fits when it opens; the batch cap is checked again here because a
  // file import can finish while the entities load.
  const handleEntitiesAdded = (result: EntitySearchResult): void => {
    const {components} = result;
    const target = entitySearchTarget;
    setEntitySearchTarget(null);
    if (target == null) {
      return;
    }
    setImportFeedback(buildSkippedEntitiesFeedback(result));
    if (components.length === 0) {
      return;
    }
    if (target.destination === 'components') {
      onInputsChange((current) =>
        validateAllInputs(
          current.map((input) =>
            input.id === target.inputId ? {...input, components: [...input.components, ...components]} : input
          )
        )
      );
      return;
    }
    const overflowCountRef = {current: null as number | null};
    let appendStartIndex = 0;
    onInputsChange((current) => {
      const combinedCount = current.length + components.length;
      if (combinedCount > maxInputs) {
        overflowCountRef.current = combinedCount;
        return current;
      }
      appendStartIndex = current.length;
      const usedNames = new Set(current.map((input) => input.name.trim()));
      return validateAllInputs([...current, ...makeEntityInputs(components, usedNames)]);
    });
    if (overflowCountRef.current != null) {
      setImportFeedback({
        level: 'error',
        title: 'Too many inputs',
        message: `Adding these entities would bring the batch to ${overflowCountRef.current} inputs, but a batch can have at most ${maxInputs}.`,
      });
      return;
    }
    // Like imported rows, new entity inputs start collapsed; jump to the page where they begin.
    setPage(Math.floor(appendStartIndex / INPUTS_PER_PAGE));
  };

  const getEntitySearchCapacity = (target: EntitySearchTarget): number => {
    if (target.destination === 'inputs') {
      return Math.max(0, maxInputs - inputs.length);
    }
    const input = inputs.find((candidate) => candidate.id === target.inputId);
    // Copies expand into repeated components on submit, so they use up the cap too.
    return Math.max(0, capabilities.maxComponents - getTotalCopies(input?.components ?? []));
  };

  const showEmptyState = inputs.length === 0 && !disabled;

  return (
    <Paper p="lg" radius="md">
      <Stack spacing="md">
        <Group align="center" noWrap={true} position="apart">
          <Group align="center" noWrap={true} spacing={4}>
            <Title mb={0} order={2}>
              Inputs
            </Title>
            <AddInputsMenu
              disabled={disabled}
              enterDataDisabled={atInputLimit}
              maxInputs={maxInputs}
              onEnterData={handleAddInput}
              onSearchEntities={withRegistryInputs ? handleSearchEntitiesForInputs : undefined}
              onUploadFile={() => {
                openFilePickerRef.current();
              }}
            />
          </Group>
          {inputs.length > 0 && (
            <Text color="dimmed" size="sm">
              {inputs.length} {pluralize(inputs.length, 'input')} · 1 prediction each
            </Text>
          )}
        </Group>
        {showEmptyState && withRegistryInputs && (
          <Stack spacing="xs">
            <Text className={classes.headerCell}>Add from Benchling</Text>
            <Group noWrap={true} spacing="sm">
              <SourceTile
                description="Each entity becomes an input"
                icon="search"
                onClick={() => {
                  handleSearchEntitiesForInputs('entities');
                }}
                title="Search entities"
              />
            </Group>
            <Text className={classes.headerCell} pt="sm">
              Or add your own
            </Text>
          </Stack>
        )}
        <InputsUploadDropzone
          disabled={disabled}
          maxInputs={maxInputs}
          onEnterManually={handleAddInput}
          onError={handleImportError}
          onImported={handleImported}
          openRef={openFilePickerRef}
          showEmptyState={showEmptyState}
          usedInputNames={usedInputNames}
        />
        {importFeedback != null && (
          <Alert
            classNames={{wrapper: classes.alertWrapper}}
            icon={<BenchlingIcon icon={IMPORT_FEEDBACK_ICON[importFeedback.level]} />}
            onClose={() => {
              setImportFeedback(null);
            }}
            title={importFeedback.title}
            variant={importFeedback.level}
            withCloseButton={true}
          >
            {importFeedback.message}
          </Alert>
        )}
        {inputs.length > 0 && (
          <div className={classes.table}>
            <div className={classes.headerRow}>
              <span aria-hidden={true} />
              <Text className={classes.headerCell}>Name</Text>
              <Text className={classes.headerCell}>Components</Text>
              <Text className={classes.headerCell}>Status</Text>
              <span aria-hidden={true} />
            </div>
            {pagedInputs.map((input, pageIndex) => {
              // Keep each row's real index so blank labels stay stable across pages.
              const index = pageStart + pageIndex;
              const expanded = expandedIds.has(input.id);
              return (
                <InputRow
                  key={input.id}
                  capabilities={capabilities}
                  components={input.components}
                  disabled={disabled}
                  expanded={expanded}
                  name={getInputLabel(input, index)}
                  nameError={input.nameError}
                  nameValue={input.name}
                  onBlur={() => {
                    handleInputBlurred(input.id);
                  }}
                  onComponentsChange={(components) => {
                    handleComponentsChange(input.id, components);
                  }}
                  onNameChange={(name) => {
                    handleNameChange(input.id, name);
                  }}
                  onRemove={() => {
                    handleRemoveInput(input.id);
                  }}
                  onSearchEntities={
                    withRegistryInputs
                      ? (tab: EntitySearchTab) => {
                          setEntitySearchTarget({destination: 'components', inputId: input.id, tab});
                        }
                      : undefined
                  }
                  onToggleExpand={() => {
                    handleToggleExpand(input.id);
                  }}
                  // Settled once collapsed or blurred at least once; only then does the "Empty" badge show.
                  settled={!expanded || blurredIds.has(input.id)}
                />
              );
            })}
            {isPaginated && (
              <div className={classes.pagerRow}>
                <InputsTablePager
                  onNext={() => {
                    setPage(Math.min(pageCount - 1, currentPage + 1));
                  }}
                  onPrev={() => {
                    setPage(Math.max(0, currentPage - 1));
                  }}
                  page={currentPage}
                  pageCount={pageCount}
                  pageEndIndex={pageStart + pagedInputs.length}
                  pageStartIndex={pageStart + 1}
                  totalCount={inputs.length}
                />
              </div>
            )}
          </div>
        )}
      </Stack>
      {entitySearchTarget != null && (
        <EntitySearchModal
          destination={entitySearchTarget.destination}
          initialTab={entitySearchTarget.tab}
          maxEntities={getEntitySearchCapacity(entitySearchTarget)}
          moleculesSupported={capabilities.supportsMolecules}
          onAdd={handleEntitiesAdded}
          onClose={() => {
            setEntitySearchTarget(null);
          }}
        />
      )}
    </Paper>
  );
}

interface SourceTileProps {
  description: string;
  icon: BenchlingIconName;
  onClick: () => void;
  title: string;
}

function SourceTile({description, icon, onClick, title}: SourceTileProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  return (
    <UnstyledButton className={classes.sourceTile} onClick={onClick}>
      <Group noWrap={true} spacing="sm">
        <BenchlingIcon className={classes.sourceTileIcon} icon={icon} />
        <Stack spacing={0}>
          <Text fw={600} size="sm">
            {title}
          </Text>
          <Text color="dimmed" size="xs">
            {description}
          </Text>
        </Stack>
      </Group>
    </UnstyledButton>
  );
}

const useStyles = createStyles((theme) => ({
  // Center the Alert icon against the whole banner (title + detail) instead of aligning it to the
  // title row. Correct for our short, at-most-two-line banners.
  alertWrapper: {
    alignItems: 'center',
  },
  table: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    columnGap: theme.spacing.md,
    display: 'grid',
    gridTemplateColumns: INPUTS_GRID_COLUMNS,
    overflow: 'hidden',
  },
  headerRow: {
    alignItems: 'center',
    backgroundColor: theme.colors.gray[0],
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridColumn: '1 / -1',
    gridTemplateColumns: 'subgrid',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
  },
  pagerRow: {
    gridColumn: '1 / -1',
  },
  sourceTile: {
    '&:hover, &:focus-visible': {
      backgroundColor: theme.colors.gray[0],
      borderColor: theme.colors.gray[5],
    },
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.md,
    maxWidth: 280,
    padding: theme.spacing.md,
  },
  sourceTileIcon: {
    color: theme.colors.blue[6],
    fontSize: 18,
  },
  headerCell: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.xs,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  },
}));
